import { Box, Typography } from "@avenue-8/sidekick-ui";
import { BrandingSectionView } from "@cma/presentation-generation-logic/models/section-view";
import { textFormatter } from "@shared/utils/text-formatter";
import { useMemo } from "react";
import MailIcon from "@mui/icons-material/Mail";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import { socialMediaConfig } from "./helpers/social-media-helpers";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import {
  AgentAvatarContainer,
  AgentInfoContent,
  BrokerageInfoContainer,
  BrokerageLogoContainer,
  ContactCard,
  ContactInfoContainer,
  ContactInfoItem,
  LeftCardInfo,
  SocialMediasContainer,
} from "./Branding.styles";

interface Props extends PresentationSectionViewProps<BrandingSectionView> {
  displayMode: "mobile" | "desktop" | "print" | "tablet";
}

export const BrandingView = (props: Props) => {
  const { sectionView } = props;
  const {
    id,
    agentName,
    agentEmail,
    agentTitle,
    agentPhoneNumber,
    agentAvatar,
    officeName,
    officeLogo,
    website,
    instagramUrl,
    facebookUrl,
    xUrl,
    youtubeUrl,
    linkedInUrl,
    preferences,
  } = sectionView;

  const {
    showAgentName,
    showAgentEmail,
    showAgentTitle,
    showOfficeName,
    showAgentPhoneNumber,
    showWebsite,
    showAgentAvatar,
  } = useMemo(() => {
    if (!preferences) return {};
    return preferences;
  }, [preferences]);

  const clickableUrl = textFormatter.formatClickableUrl(website ?? null);
  const prettyUrl = textFormatter.formatPrettyUrl(website ?? null);

  const socialMediaFields = {
    instagramUrl,
    facebookUrl,
    xUrl,
    youtubeUrl,
    linkedInUrl,
  };

  return (
    <Box id={id} py={3} px={3} mt={3} textAlign="center">
      <ContactCard>
        <LeftCardInfo>
          {(!!officeLogo || showOfficeName) && (
            <BrokerageInfoContainer>
              <BrokerageLogoContainer>
                {!!officeLogo && clickableUrl ? (
                  <a
                    href={clickableUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit brokerage website"
                  >
                    <img
                      src={officeLogo}
                      alt={officeName ?? "Brokerage logo"}
                    />
                  </a>
                ) : (
                  !!officeLogo && (
                    <img
                      src={officeLogo}
                      alt={officeName ?? "Brokerage logo"}
                    />
                  )
                )}
              </BrokerageLogoContainer>
              {showOfficeName && officeName && (
                <Typography variant="h5">{officeName}</Typography>
              )}
            </BrokerageInfoContainer>
          )}

          <AgentInfoContent>
            {showAgentName && agentName && (
              <>
                <Typography variant="h6">{agentName}</Typography>
                {showAgentTitle && agentTitle && (
                  <Typography variant="body2">{agentTitle}</Typography>
                )}
              </>
            )}

            <ContactInfoContainer>
              {showAgentEmail && agentEmail && (
                <ContactInfoItem>
                  <MailIcon fontSize="small" />
                  <a href={`mailto:${agentEmail}`}>{agentEmail}</a>
                </ContactInfoItem>
              )}

              {showAgentPhoneNumber && agentPhoneNumber && (
                <ContactInfoItem>
                  <PhoneIcon fontSize="small" />
                  <a href={`tel:${agentPhoneNumber}`}>
                    {textFormatter.formatPhoneNumber(agentPhoneNumber)}
                  </a>
                </ContactInfoItem>
              )}

              {showWebsite && clickableUrl && prettyUrl && (
                <ContactInfoItem>
                  <LanguageIcon fontSize="small" />
                  <a
                    href={clickableUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {prettyUrl}
                  </a>
                </ContactInfoItem>
              )}
            </ContactInfoContainer>
          </AgentInfoContent>

          <SocialMediasContainer>
            {Object.entries(socialMediaConfig).map(
              ([key, { name, SvgIcon, extractHandle, handleToUrl }]) => {
                const handle = extractHandle(socialMediaFields[name] || "");
                const url = handleToUrl(handle || "");

                if (!handle?.trim() || !url) return null;

                return (
                  <ContactInfoItem key={key}>
                    <SvgIcon fontSize="small" />
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <span>{handle}</span>
                    </a>
                  </ContactInfoItem>
                );
              }
            )}
          </SocialMediasContainer>
        </LeftCardInfo>

        {showAgentAvatar && agentAvatar && (
          <AgentAvatarContainer>
            <img src={agentAvatar} alt={agentName ?? "Agent profile photo"} />
          </AgentAvatarContainer>
        )}
      </ContactCard>
    </Box>
  );
};
